import React from "react";
import { CustomCheckBox } from "../../../components/utils/CustomCheckBox";
import Select from "../../../components/utils/Select";
import { OmeApplicationLintResult } from "../../../interfaces/omeApplication";
import {
  ADD_SKELETONS,
  FIELD_SPECS,
  FieldSpec,
  NodeContext,
  PROVIDERS,
  PUBLISHERS,
  PUBLISHERS_ROOT_KEYS,
  PresenceSpec,
} from "../schema";
import styles from "./app-config-editor.module.css";

// Typed, carded editor over the canonical app-config tree. Known fields (per the
// schema registry) render as the right control — checkbox / dropdown / number /
// dynamic reference picker. Unknown fields fall through to a generic text-input
// recursion so the full schema stays editable and nothing is silently dropped.
// Values stay strings, exactly as the engine reports them; the middleware owns
// validation. Absent optional fields are NOT materialized until touched, so an
// untouched app regenerates identically (no false drift / pending).

export type ConfigPath = (string | number)[];

const setAtPath = (root: any, path: ConfigPath, value: any): any => {
  if (!path.length) return value;
  const [head, ...rest] = path;
  // When the container at this level doesn't exist yet, its type is dictated by
  // the key: a numeric index means we're building an array, a string key an
  // object. Blindly defaulting to {} turned array-first-inserts into {"0": …},
  // which then broke every `.map` over that path (e.g. adding the first output
  // profile / encode / rendition to a freshly created app with no such branch).
  const base = root ?? (typeof head === "number" ? [] : {});
  const clone = Array.isArray(base) ? [...base] : { ...base };
  clone[head as any] = setAtPath(base[head as any], rest, value);
  return clone;
};

const getAtPath = (root: any, path: ConfigPath): any => {
  let current = root;
  for (const key of path) current = current?.[key];
  return current;
};

const removeAtPath = (root: any, path: ConfigPath): any => {
  const parentPath = path.slice(0, -1);
  const key = path[path.length - 1];
  const parent = getAtPath(root, parentPath);
  if (Array.isArray(parent)) {
    const cloned = parent.filter((_, index) => index !== key);
    return setAtPath(root, parentPath, cloned);
  }
  const cloned = { ...(parent ?? {}) };
  delete cloned[key as any];
  return setAtPath(root, parentPath, cloned);
};

const clone = (value: any) => JSON.parse(JSON.stringify(value ?? {}));
const isBoolTrue = (value: any) => value === true || value === "true";
const isEmptyObject = (value: any) =>
  value == null || (typeof value === "object" && !Array.isArray(value) && Object.keys(value).length === 0) || (Array.isArray(value) && value.length === 0);

interface Handlers {
  onSet: (path: ConfigPath, value: any) => void;
  onRemove: (path: ConfigPath) => void;
}

interface RefOptions {
  video: { value: string; label: string }[];
  audio: { value: string; label: string }[];
}

// ---------------------------------------------------------------- primitives

const Field = ({ spec, children }: { spec: FieldSpec; children: React.ReactNode }) => (
  <div className={styles.field}>
    <div className={styles.fieldLabel}>
      {spec.label}
      {spec.unit ? <span className={styles.unit}> ({spec.unit})</span> : null}
    </div>
    <div className={styles.fieldControl}>{children}</div>
    {spec.help ? <div className={styles.help}>{spec.help}</div> : null}
  </div>
);

const Collapsible = ({ label, defaultOpen, children }: { label: string; defaultOpen?: boolean; children: React.ReactNode }) => (
  <details className={styles.collapsible} open={defaultOpen}>
    <summary className={styles.collapsibleSummary}>{label}</summary>
    <div className={styles.collapsibleBody}>{children}</div>
  </details>
);

const Section = ({ title, summary, defaultOpen, children }: { title: string; summary?: string; defaultOpen?: boolean; children: React.ReactNode }) => (
  <details className={styles.section} open={defaultOpen}>
    <summary className={styles.sectionSummary}>
      <span className={styles.sectionTitle}>{title}</span>
      {summary ? <span className={styles.sectionSummaryText}>{summary}</span> : null}
    </summary>
    <div className={styles.sectionBody}>{children}</div>
  </details>
);

const Card = ({ title, tone, actions, children }: { title: string; tone?: "sub" | "sub2"; actions?: React.ReactNode; children: React.ReactNode }) => {
  const classes = [styles.card];
  if (tone === "sub") classes.push(styles.cardSub);
  if (tone === "sub2") classes.push(styles.cardSub2);
  return (
    <div className={classes.join(" ")}>
      <div className={styles.cardHeader}>
        <span className={styles.cardTitle}>{title}</span>
        {actions ? <span className={styles.cardActions}>{actions}</span> : null}
      </div>
      <div className={styles.cardBody}>{children}</div>
    </div>
  );
};

const IconButton = ({ icon, title, onClick, danger }: { icon: string; title: string; onClick: () => void; danger?: boolean }) => (
  <button type="button" className={danger ? styles.iconButtonDanger : styles.iconButton} title={title} onClick={onClick}>
    {icon}
  </button>
);

const AddButton = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <button type="button" className={styles.addRowButton} onClick={onClick}>
    {`+ ${label}`}
  </button>
);

// ------------------------------------------------------------ typed control

interface TypedFieldProps extends Handlers {
  spec: FieldSpec;
  obj: any;
  path: ConfigPath;
  disabled?: boolean;
  refOptions?: RefOptions;
}

const TypedField = ({ spec, obj, path, onSet, onRemove, disabled, refOptions }: TypedFieldProps) => {
  const raw = obj?.[spec.key];
  const fieldPath = [...path, spec.key];
  const set = (value: any) => onSet(fieldPath, value);
  const removeKey = () => onRemove(fieldPath);
  const setOrUnset = (value: string) => (value === "" ? removeKey() : set(value));

  let control: React.ReactNode;
  switch (spec.control) {
    case "checkbox":
      control = (
        <CustomCheckBox isSelected={isBoolTrue(raw)} disabled={disabled} setSelected={(on) => set(on ? "true" : "false")} />
      );
      break;
    case "number":
      control = (
        <input
          type="number"
          className={styles.input}
          value={raw != null ? String(raw) : ""}
          disabled={disabled}
          onChange={(event) => setOrUnset(event.currentTarget.value)}
        />
      );
      break;
    case "select":
      control = (
        <Select value={raw != null ? String(raw) : ""} options={(spec.options ?? []).map((o) => ({ value: o, label: o }))} disabled={disabled} onValue={setOrUnset} />
      );
      break;
    case "ref-select": {
      const options = spec.refSource === "audio-encodes" ? refOptions?.audio ?? [] : refOptions?.video ?? [];
      const current = raw != null ? String(raw) : "";
      const stale = current !== "" && !options.some((o) => o.value === current);
      control = <Select value={current} options={options} placeholder="— none —" invalid={stale} disabled={disabled} onValue={setOrUnset} />;
      break;
    }
    case "url-list": {
      const list: string[] = Array.isArray(raw) ? raw : [];
      control = (
        <div className={styles.urlList}>
          {list.map((entry, index) => (
            <div key={index} className={styles.urlRow}>
              <input className={styles.input} value={entry} disabled={disabled} onChange={(event) => onSet([...fieldPath, index], event.currentTarget.value)} />
              {!disabled ? <IconButton icon="×" title="Remove" onClick={() => onRemove([...fieldPath, index])} danger /> : null}
            </div>
          ))}
          {!disabled ? <AddButton label="entry" onClick={() => onSet([...fieldPath, list.length], "*")} /> : null}
        </div>
      );
      break;
    }
    case "template":
    case "text":
    default:
      control = (
        <input
          type="text"
          className={spec.control === "template" ? styles.templateInput : styles.input}
          value={raw != null ? String(raw) : ""}
          placeholder={spec.placeholder}
          disabled={disabled}
          onChange={(event) => setOrUnset(event.currentTarget.value)}
        />
      );
      break;
  }

  return <Field spec={spec}>{control}</Field>;
};

// ---------------------------------------------------- typed object fields

interface TypedObjectFieldsProps extends Handlers {
  context: NodeContext;
  value: any;
  path: ConfigPath;
  // Keys handled specially by the parent (nested cards/arrays) — excluded from
  // the generic "other fields" fallback so they aren't rendered twice.
  extraExclude?: string[];
  refOptions?: RefOptions;
  // When true, disable every value field except name/bypass (bypass passthrough).
  disableValues?: boolean;
}

const TypedObjectFields = ({ context, value, path, onSet, onRemove, extraExclude, refOptions, disableValues }: TypedObjectFieldsProps) => {
  const specs = FIELD_SPECS[context] ?? [];
  const common = specs.filter((spec) => !spec.advanced);
  const advanced = specs.filter((spec) => spec.advanced);
  const handled = new Set<string>([...specs.map((spec) => spec.key), ...(extraExclude ?? [])]);
  const extraKeys =
    value && typeof value === "object" && !Array.isArray(value) ? Object.keys(value).filter((key) => !handled.has(key)) : [];

  const fieldDisabled = (spec: FieldSpec) => disableValues && spec.key !== "name" && spec.key !== "bypass";

  return (
    <>
      {common.length ? (
        <div className={styles.fieldGrid}>
          {common.map((spec) => (
            <TypedField key={spec.key} spec={spec} obj={value} path={path} onSet={onSet} onRemove={onRemove} disabled={fieldDisabled(spec)} refOptions={refOptions} />
          ))}
        </div>
      ) : null}
      {advanced.length ? (
        <Collapsible label={`Show advanced (${advanced.length})`}>
          <div className={styles.fieldGrid}>
            {advanced.map((spec) => (
              <TypedField key={spec.key} spec={spec} obj={value} path={path} onSet={onSet} onRemove={onRemove} disabled={fieldDisabled(spec)} refOptions={refOptions} />
            ))}
          </div>
        </Collapsible>
      ) : null}
      {extraKeys.length ? (
        <div className={styles.extraGroup}>
          <div className={styles.extraLabel}>{"Other fields" /* TRANSLATION */}</div>
          {extraKeys.map((key) => (
            <GenericNode key={key} label={key} value={value[key]} path={[...path, key]} onSet={onSet} onRemove={onRemove} depth={2} />
          ))}
        </div>
      ) : null}
    </>
  );
};

// --------------------------------------------------------------- encodes

// Engine auto-name counter increments only for UNNAMED encodes (encodes.h):
// [{name:"x"}, {unnamed}] -> the unnamed one is video_0, not video_1.
const refOptionsFor = (items: any[] | undefined, prefix: string) => {
  let autoIndex = 0;
  return (items ?? []).map((encode, index) => {
    const named = encode?.name != null && String(encode.name) !== "";
    const value = named ? String(encode.name) : `${prefix}_${autoIndex++}`;
    return { value, label: named ? value : `${value} (unnamed #${index})` };
  });
};

interface EncodeArrayProps extends Handlers {
  label: string;
  addLabel: string;
  prefix: string;
  items: any[] | undefined;
  path: ConfigPath;
  context: NodeContext;
  bypassContext?: NodeContext;
  skeleton: () => any;
}

const EncodeArray = ({ label, addLabel, prefix, items, path, context, bypassContext, skeleton, onSet, onRemove }: EncodeArrayProps) => {
  const list = items ?? [];
  return (
    <div className={styles.subGroup}>
      <div className={styles.subGroupHeader}>
        <span className={styles.subGroupTitle}>{`${label} [${list.length}]`}</span>
        <AddButton label={addLabel} onClick={() => onSet([...path, list.length], skeleton())} />
      </div>
      {list.map((item, index) => {
        const bypass = isBoolTrue(item?.bypass);
        const named = item?.name != null && String(item.name) !== "";
        const title = named ? String(item.name) : `#${index} (${prefix}_${index})`;
        const itemPath = [...path, index];
        return (
          <Card
            key={index}
            tone="sub"
            title={title}
            actions={
              <>
                <IconButton icon="⧉" title="Duplicate" onClick={() => onSet([...path, list.length], clone(item))} />
                <IconButton
                  icon="×"
                  title="Remove"
                  danger
                  onClick={() => {
                    if (isEmptyObject(item) || window.confirm(`Remove ${title}?`)) onRemove(itemPath);
                  }}
                />
              </>
            }
          >
            <TypedObjectFields context={context} value={item} path={itemPath} onSet={onSet} onRemove={onRemove} extraExclude={bypassContext ? ["bypassIfMatch"] : []} disableValues={bypass} />
            {bypassContext ? (
              <Collapsible label="Bypass-if-match">
                <TypedObjectFields context={bypassContext} value={item?.bypassIfMatch ?? {}} path={[...itemPath, "bypassIfMatch"]} onSet={onSet} onRemove={onRemove} />
              </Collapsible>
            ) : null}
          </Card>
        );
      })}
    </div>
  );
};

// -------------------------------------------------------------- renditions

interface RenditionArrayProps extends Handlers {
  renditions: any[] | undefined;
  path: ConfigPath;
  refOptions: RefOptions;
}

const RenditionArray = ({ renditions, path, refOptions, onSet, onRemove }: RenditionArrayProps) => {
  const list = renditions ?? [];
  return (
    <div className={styles.subGroup}>
      <div className={styles.subGroupHeader}>
        <span className={styles.subGroupTitle}>{`Renditions [${list.length}]`}</span>
        <AddButton label="rendition" onClick={() => onSet([...path, list.length], ADD_SKELETONS.rendition())} />
      </div>
      {list.map((rendition, index) => {
        const title = rendition?.name != null && String(rendition.name) !== "" ? String(rendition.name) : `#${index}`;
        const itemPath = [...path, index];
        return (
          <Card
            key={index}
            tone="sub2"
            title={title}
            actions={
              <>
                <IconButton icon="⧉" title="Duplicate" onClick={() => onSet([...path, list.length], clone(rendition))} />
                <IconButton icon="×" title="Remove" danger onClick={() => { if (isEmptyObject(rendition) || window.confirm(`Remove ${title}?`)) onRemove(itemPath); }} />
              </>
            }
          >
            <TypedObjectFields context="rendition" value={rendition} path={itemPath} onSet={onSet} onRemove={onRemove} refOptions={refOptions} />
          </Card>
        );
      })}
    </div>
  );
};

// --------------------------------------------------------------- playlists

interface PlaylistArrayProps extends Handlers {
  playlists: any[] | undefined;
  path: ConfigPath;
  refOptions: RefOptions;
}

const PlaylistArray = ({ playlists, path, refOptions, onSet, onRemove }: PlaylistArrayProps) => {
  const list = playlists ?? [];
  return (
    <div className={styles.subGroup}>
      <div className={styles.subGroupHeader}>
        <span className={styles.subGroupTitle}>{`Playlists [${list.length}]`}</span>
        <AddButton label="playlist" onClick={() => onSet([...path, list.length], ADD_SKELETONS.playlist())} />
      </div>
      {list.map((playlist, index) => {
        const title = playlist?.name != null && String(playlist.name) !== "" ? String(playlist.name) : `#${index}`;
        const itemPath = [...path, index];
        return (
          <Card
            key={index}
            tone="sub"
            title={`Playlist: ${title}`}
            actions={
              <>
                <IconButton icon="⧉" title="Duplicate" onClick={() => onSet([...path, list.length], clone(playlist))} />
                <IconButton icon="×" title="Remove" danger onClick={() => { if (isEmptyObject(playlist) || window.confirm(`Remove ${title}?`)) onRemove(itemPath); }} />
              </>
            }
          >
            <TypedObjectFields context="playlist" value={playlist} path={itemPath} onSet={onSet} onRemove={onRemove} extraExclude={["options", "renditions"]} />
            <Collapsible label="Options">
              <TypedObjectFields context="playlist-options" value={playlist?.options ?? {}} path={[...itemPath, "options"]} onSet={onSet} onRemove={onRemove} />
            </Collapsible>
            <RenditionArray renditions={playlist?.renditions} path={[...itemPath, "renditions"]} refOptions={refOptions} onSet={onSet} onRemove={onRemove} />
          </Card>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------- output profiles

const ProfileCard = ({ profile, index, path, onSet, onRemove }: Handlers & { profile: any; index: number; path: ConfigPath }) => {
  const encodes = profile?.encodes ?? {};
  const refOptions: RefOptions = {
    video: refOptionsFor(encodes.videos, "video"),
    audio: refOptionsFor(encodes.audios, "audio"),
  };
  const title = profile?.name != null && String(profile.name) !== "" ? String(profile.name) : `#${index}`;
  return (
    <Card
      title={`Profile: ${title}`}
      actions={
        <>
          <IconButton icon="⧉" title="Duplicate" onClick={() => onSet([...path.slice(0, -1), (path[path.length - 1] as number) + 1], clone(profile))} />
          <IconButton icon="×" title="Remove" danger onClick={() => { if (isEmptyObject(profile) || window.confirm(`Remove profile ${title}?`)) onRemove(path); }} />
        </>
      }
    >
      <TypedObjectFields context="output-profile" value={profile} path={path} onSet={onSet} onRemove={onRemove} extraExclude={["encodes", "playlists"]} />
      <div className={styles.subGroup}>
        <div className={styles.subGroupTitle}>{"Encodes" /* TRANSLATION */}</div>
        <EncodeArray label="Video" addLabel="video encode" prefix="video" items={encodes.videos} path={[...path, "encodes", "videos"]} context="encode-video" bypassContext="bypassIfMatch-video" skeleton={ADD_SKELETONS.videoEncode} onSet={onSet} onRemove={onRemove} />
        <EncodeArray label="Audio" addLabel="audio encode" prefix="audio" items={encodes.audios} path={[...path, "encodes", "audios"]} context="encode-audio" bypassContext="bypassIfMatch-audio" skeleton={ADD_SKELETONS.audioEncode} onSet={onSet} onRemove={onRemove} />
        <EncodeArray label="Image" addLabel="image encode" prefix="image" items={encodes.images} path={[...path, "encodes", "images"]} context="encode-image" skeleton={ADD_SKELETONS.imageEncode} onSet={onSet} onRemove={onRemove} />
      </div>
      <PlaylistArray playlists={profile?.playlists} path={[...path, "playlists"]} refOptions={refOptions} onSet={onSet} onRemove={onRemove} />
    </Card>
  );
};

// ------------------------------------------------------ providers / publishers

interface PresenceGroupProps extends Handlers {
  specs: PresenceSpec[];
  value: any;
  path: ConfigPath;
  reservedKeys?: string[];
}

const PresenceGroup = ({ specs, value, path, reservedKeys, onSet, onRemove }: PresenceGroupProps) => {
  const obj = value ?? {};
  const known = new Set<string>([...specs.map((spec) => spec.key), ...(reservedKeys ?? [])]);
  const extraKeys = Object.keys(obj).filter((key) => !known.has(key));
  return (
    <div className={styles.presenceGroup}>
      <div className={styles.presenceRow}>
        {specs.map((spec) => {
          const present = obj[spec.key] !== undefined;
          return (
            <label key={spec.key} className={styles.presenceItem}>
              <CustomCheckBox
                isSelected={present}
                setSelected={(on) => {
                  if (on) onSet([...path, spec.key], {});
                  else if (isEmptyObject(obj[spec.key]) || window.confirm(`Disable ${spec.label} and drop its settings?`)) onRemove([...path, spec.key]);
                }}
              />
              <span>{spec.label}</span>
            </label>
          );
        })}
      </div>
      {specs
        .filter((spec) => spec.nested && obj[spec.key] !== undefined)
        .map((spec) => (
          <Collapsible key={spec.key} label={spec.label} defaultOpen>
            <TypedObjectFields context={spec.nested as NodeContext} value={obj[spec.key]} path={[...path, spec.key]} onSet={onSet} onRemove={onRemove} />
          </Collapsible>
        ))}
      {extraKeys.length ? (
        <div className={styles.extraGroup}>
          <div className={styles.extraLabel}>{"Other" /* TRANSLATION */}</div>
          {extraKeys.map((key) => (
            <GenericNode key={key} label={key} value={obj[key]} path={[...path, key]} onSet={onSet} onRemove={onRemove} depth={2} />
          ))}
        </div>
      ) : null}
    </div>
  );
};

// -------------------------------------------------------- generic fallback

// Kept for anything the registry does not describe: an unknown top-level key, an
// exotic provider/publisher, or a construct the managed model does not model.
// Every leaf is a plain text field; nothing is dropped.

interface GenericNodeProps extends Handlers {
  label: string;
  value: any;
  path: ConfigPath;
  depth: number;
}

const GenericNode = ({ label, value, path, onSet, onRemove, depth }: GenericNodeProps) => {
  if (value === null || typeof value !== "object") {
    return (
      <div className={styles.scalarRow}>
        <span className={styles.scalarLabel}>{label}</span>
        <input className={styles.scalarInput} value={String(value ?? "")} onChange={(event) => onSet(path, event.currentTarget.value)} />
        <IconButton icon="×" title="Remove field" onClick={() => onRemove(path)} danger />
      </div>
    );
  }
  if (Array.isArray(value)) {
    return (
      <fieldset className={styles.group}>
        <legend className={styles.groupLegend}>
          {label} [{value.length}]
          <AddButton label="item" onClick={() => onSet([...path, value.length], value.length ? clone(value[value.length - 1]) : {})} />
        </legend>
        {value.map((item, index) => (
          <GenericNode key={index} label={`#${index}${item && typeof item === "object" && item.name ? ` — ${item.name}` : ""}`} value={item} path={[...path, index]} onSet={onSet} onRemove={onRemove} depth={depth + 1} />
        ))}
      </fieldset>
    );
  }
  return (
    <fieldset className={styles.group}>
      <legend className={styles.groupLegend}>
        {label}
        {depth > 0 ? <IconButton icon="×" title="Remove" onClick={() => onRemove(path)} danger /> : null}
      </legend>
      {Object.entries(value).map(([key, child]) => (
        <GenericNode key={key} label={key} value={child} path={[...path, key]} onSet={onSet} onRemove={onRemove} depth={depth + 1} />
      ))}
      <GenericAddField onAdd={(key, initial) => onSet([...path, key], initial)} />
    </fieldset>
  );
};

const GenericAddField = ({ onAdd }: { onAdd: (key: string, initial: any) => void }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [kind, setKind] = React.useState<"value" | "section" | "list">("value");
  const add = () => {
    const key = inputRef.current?.value?.trim();
    if (!key) return;
    onAdd(key, kind === "value" ? "" : kind === "list" ? [] : {});
    if (inputRef.current) inputRef.current.value = "";
  };
  return (
    <div className={styles.addFieldRow}>
      <input ref={inputRef} className={styles.addFieldInput} placeholder={"field name" /* TRANSLATION */} />
      <select className={styles.addFieldSelect} value={kind} onChange={(event) => setKind(event.currentTarget.value as any)}>
        <option value="value">value</option>
        <option value="section">section</option>
        <option value="list">list</option>
      </select>
      <button type="button" className={styles.addButton} onClick={add}>
        +
      </button>
    </div>
  );
};

// ------------------------------------------------------------------- root

const presenceSummary = (obj: any, specs: PresenceSpec[]) => {
  const present = specs.filter((spec) => obj?.[spec.key] !== undefined).map((spec) => spec.label);
  return present.length ? present.join("  ·  ") : "—";
};

export interface AppConfigEditorProps {
  config: any;
  onChange: (config: any) => void;
  lintResults?: OmeApplicationLintResult[];
}

const TOP_LEVEL_KNOWN = new Set(["name", "type", "outputProfiles", "providers", "publishers", "dynamic"]);

const AppConfigEditor = ({ config, onChange, lintResults }: AppConfigEditorProps) => {
  const onSet = (path: ConfigPath, value: any) => onChange(setAtPath(config, path, value));
  const onRemove = (path: ConfigPath) => onChange(removeAtPath(config, path));

  const cfg = config ?? {};
  const profiles: any[] = cfg.outputProfiles?.outputProfile ?? [];
  const otherKeys = Object.keys(cfg).filter((key) => !TOP_LEVEL_KNOWN.has(key));

  return (
    <div className={styles.container}>
      <Section title={"General" /* TRANSLATION */} defaultOpen>
        <div className={styles.fieldGrid}>
          <Field spec={{ key: "name", label: "Name", control: "text" }}>
            <input className={styles.input} value={String(cfg.name ?? "")} disabled />
          </Field>
          <TypedField spec={FIELD_SPECS["app-root"][0]} obj={cfg} path={[]} onSet={onSet} onRemove={onRemove} />
        </div>
      </Section>

      <Section title={"Providers" /* TRANSLATION */} summary={presenceSummary(cfg.providers, PROVIDERS)}>
        <PresenceGroup specs={PROVIDERS} value={cfg.providers} path={["providers"]} onSet={onSet} onRemove={onRemove} />
      </Section>

      <Section title={"Publishers" /* TRANSLATION */} summary={presenceSummary(cfg.publishers, PUBLISHERS)}>
        <div className={styles.fieldGrid}>
          {FIELD_SPECS["publishers-root"].map((spec) => (
            <TypedField key={spec.key} spec={spec} obj={cfg.publishers ?? {}} path={["publishers"]} onSet={onSet} onRemove={onRemove} />
          ))}
        </div>
        <PresenceGroup specs={PUBLISHERS} value={cfg.publishers} path={["publishers"]} reservedKeys={PUBLISHERS_ROOT_KEYS} onSet={onSet} onRemove={onRemove} />
      </Section>

      <Section title={`${"Output Profiles" /* TRANSLATION */} [${profiles.length}]`} defaultOpen>
        {profiles.map((profile, index) => (
          <ProfileCard key={index} profile={profile} index={index} path={["outputProfiles", "outputProfile", index]} onSet={onSet} onRemove={onRemove} />
        ))}
        <AddButton label="output profile" onClick={() => onSet(["outputProfiles", "outputProfile", profiles.length], ADD_SKELETONS.outputProfile())} />
      </Section>

      {otherKeys.length ? (
        <Section title={"Other" /* TRANSLATION */}>
          {otherKeys.map((key) => (
            <GenericNode key={key} label={key} value={cfg[key]} path={[key]} onSet={onSet} onRemove={onRemove} depth={1} />
          ))}
        </Section>
      ) : null}

      {lintResults?.length ? (
        <div className={styles.lintPanel}>
          {lintResults.map((result, index) => (
            <div key={index} className={result.severity === "error" ? styles.lintError : styles.lintWarn}>
              [{result.severity}] {result.id} — {result.message}
            </div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default AppConfigEditor;
